import useStructureSearch from '@data/query/useStructureSearch';
import {
  EuiCallOut,
  EuiComboBox,
  EuiComboBoxOptionOption,
  EuiContext,
  EuiSpacer,
  EuiText,
  EuiButtonIcon,
} from '@elastic/eui';
import { StructType, Structure } from '@lib/domain/stuctures';
import debounce from 'lodash/debounce';
import { FunctionComponent, useState, useEffect, useContext } from 'react';
import useGetPvtVds from '@data/query/useGetPvtVds';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { PVTContext } from '@lib/context/pvt.context';

const typeLabels: Record<StructType, string> = {
  country: 'Country',
  province: 'Province',
  region: 'Region',
  municipality: 'Municipality',
  constituency: 'Constituency',
  ward: 'Ward',
  votingdistrict: 'VD',
};

type StructureOption = EuiComboBoxOptionOption<any>;
interface StructureSearchProps {
  value: Structure[];
  onChange: (value: Structure[]) => void;
  disabled?: boolean;
  placeholder?: string;
  limitToSameLevel?: boolean;
  types?: string[];
  singleSelection?: boolean;
  redirect?: boolean;
}
interface VDCode {
  code: string | null;
  refresh: number | null;
}

const StructureFind: FunctionComponent<StructureSearchProps> = props => {
  const {
    value: selected,
    onChange,
    disabled = false,
    placeholder,
    singleSelection,
    redirect = true,
  } = props;

  const [vdCode, setVDCode] = useState<VDCode>({ code: null, refresh: null });
  const { electionId } = useContext(PVTContext);

  const {
    getPvtVds,
    loading: loadingPvt,
    error: errorPvt,
  } = useGetPvtVds(
    { type: 'votingdistrict', code: vdCode?.code },
    electionId,
    10,
    0,
    null,
    null
  );

  useEffect(() => {
    if (!vdCode.code || loadingPvt || errorPvt || !getPvtVds) return;

    window.open(`/vd/${getPvtVds[0]?.id}`);
  }, [vdCode?.refresh, loadingPvt, errorPvt, getPvtVds]);

  const structureOption = (structure: Structure): StructureOption => ({
    label: `${typeLabels[structure.type]}: ${structure.name}`,
    value: structure,
    append:
      structure.type === 'votingdistrict' && redirect ? (
        <EuiButtonIcon
          iconType={FaExternalLinkAlt}
          onClick={e => {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
            setVDCode({ code: structure.code, refresh: Math.random() });
          }}
        />
      ) : null,
  });

  const [searchValue, setSearchValue] = useState('');
  const { loading, error, structures } = useStructureSearch(
    searchValue
    // types ?? limitByType
  );

  const debouncedHandleSearchChange = debounce((value: string) => {
    setSearchValue(value);
  }, 300);

  const handleSearchChange = (value: string) => {
    debouncedHandleSearchChange(value?.trimEnd());
  };

  // remove structures that are already selected from the search results
  const filteredStructures = structures?.filter(
    opt => !selected.some(sel => opt.type === sel.type && opt.code === sel.code)
  );

  const renderOption = (option: StructureOption) => {
    const { value: structure } = option;

    const structType = typeLabels[structure.type];
    const province = structure.parents?.find(p => p.type === 'province')?.name;

    return (
      <div>
        <EuiText size="xs">{structure.name}</EuiText>
        <EuiText size="xs" color="subdued">
          {province ? `${structType}, ${province}` : structType}
        </EuiText>
      </div>
    );
  };
  const _placeholder = placeholder ?? 'Start typing to search for a structure';
  return (
    <>
      {error && (
        <EuiCallOut
          title="Structure Search Error"
          color="danger"
          iconType="alert">
          {error?.message}
        </EuiCallOut>
      )}
      <EuiSpacer size="s" />
      <EuiContext
        i18n={{
          mapping: {
            'euiComboBoxOptionsList.noMatchingOptions': `${searchValue} doesn't match any options you have access to`,
          },
        }}>
        <EuiComboBox
          fullWidth
          async
          isLoading={loading}
          isDisabled={disabled}
          noSuggestions={searchValue.trim().length < 3}
          aria-label={_placeholder}
          placeholder={_placeholder}
          singleSelection={singleSelection ? { asPlainText: true } : false}
          selectedOptions={selected?.map(s => structureOption(s))}
          options={
            searchValue.trim().length >= 3
              ? filteredStructures?.map(s => structureOption(s))
              : []
          }
          onChange={options => {
            const votingDistrict = options.find(
              structure => structure.value.type === 'votingdistrict'
            );
            console.log('[options]', votingDistrict);
            onChange(options.map(o => o.value));
          }}
          rowHeight={50}
          onSearchChange={handleSearchChange}
          renderOption={renderOption}
          isClearable={true}
        />
      </EuiContext>
    </>
  );
};

export default StructureFind;
